/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = Number(new URLSearchParams(document.location.search).get('page') || 1) + 1;
    }
    connectedCallback() {
      if (this.cbmConnected) {
        this.observer?.observe(this);
        document.addEventListener('collection:filter-start', this.cbmFilterStart);
        if (this.cbmStampedReady) document.addEventListener('stamped:reviews:loaded', this.cbmStampedReady);
        return;
      }
      this.cbmConnected = true;
      this.cbmFilterStart = () => {
        this.cbmRequest?.abort();
        this.cbmRequest = null;
        this.removeAttribute('loading');
      };
      document.addEventListener('collection:filter-start', this.cbmFilterStart);
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.cbmRequest?.abort();
      this.cbmRequest = null;
      document.removeEventListener('collection:filter-start', this.cbmFilterStart);
      if (this.cbmStampedReady) document.removeEventListener('stamped:reviews:loaded', this.cbmStampedReady);
    }
    refreshReviewBadges() {
      if (typeof window.StampedFn?.loadBadges === 'function') {
        if (this.cbmStampedReady) document.removeEventListener('stamped:reviews:loaded', this.cbmStampedReady);
        this.cbmStampedReady = null;
        window.StampedFn.loadBadges();
      } else if (!this.cbmStampedReady) {
        this.cbmStampedReady = () => {
          if (this.isConnected) this.refreshReviewBadges();
        };
        document.addEventListener('stamped:reviews:loaded', this.cbmStampedReady);
      }
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function () {
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    async loadProducts() {
      if (this.getAttribute('loading') || this.cbmFinished || document.querySelector('#ProductGridContainer .collection.loading')) return;
      const page = this.i;
      const url = document.location.pathname + this.addUrlParam(document.location.search, 'page');
      const grid = this.grid;
      const controller = new AbortController();
      this.cbmRequest = controller;
      this.setAttribute('loading', true);
      this.querySelector('[data-pagination-error]')?.remove();
      let timer;
      try {
        const html = await Promise.race([
          fetch(url, { signal: controller.signal }).then(response => {
            if (!response.ok) throw new Error('pagination-request-failed');
            return response.text();
          }),
          new Promise((resolve, reject) => { timer = setTimeout(() => {
            controller.abort(); reject(new Error('pagination-timeout'));
          }, 8000); })
        ]);
        if (!this.isConnected || this.cbmRequest !== controller || this.grid !== grid || !grid.isConnected) return;
        this.renderProducts(html, url);
        this.i = page + 1;
        dispatchCustomEvent('pagination:page-changed', { url });
      } catch (error) {
        if (!this.isConnected || this.cbmRequest !== controller) return;
        const feedback = document.createElement('p');
        feedback.setAttribute('data-pagination-error', '');
        feedback.setAttribute('role', 'alert');
        feedback.append('Unable to load more products. ');
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Try again';
        retry.addEventListener('click', () => this.loadProducts());
        feedback.append(retry);
        this.append(feedback);
      } finally {
        clearTimeout(timer);
        if (this.cbmRequest === controller) {
          this.cbmRequest = null;
          this.removeAttribute('loading');
        }
      }
    }
    renderProducts(html, url) {
      let base = this,
        grid_to_replace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');

      if (!grid_to_replace) throw new Error('pagination-grid-missing');
      let products = grid_to_replace.querySelectorAll('.column');
      if (!products.length) {
        this.cbmFinished = true;
        base.observer?.unobserve(base);
        base.button?.classList.add('visually-hidden');
        return;
      }
      for (var i = 0; i < products.length; i++) {
        this.grid.appendChild(products[i]);
      }
      if (this.animations_enabled) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.refreshReviewBadges();
          }
        });
      } else {
        base.refreshReviewBadges();
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}

